import React, { useEffect, useRef, useState } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Badge,
    Tabs,
    Tab,
    Alert,
} from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cameFromState } from '@/features/applications/faculty/backLink';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import useConsumeLocationState from '@/shared/hooks/useConsumeLocationState';
import applicationService from '@/shared/services/applicationService';
import studentApplicationService from '@/shared/services/studentApplicationService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { resolveRoles } from '@/shared/utils/previewRoles';
import { applyEntryHref } from '@/shared/utils/positionsAccess';
import { useTerm } from '@/shared/contexts/TermContext';
import { termDisplayName } from '@/shared/utils/termLabel';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import { StudentApplicationsNotOpen } from '@/components/common/ProgramPageLinks';
import DeleteDraftButton from './faculty/DeleteDraftButton';
import StartRevisionButton from './faculty/StartRevisionButton';
import { resumeDraftPath } from './faculty/draftApplication';
import { badgeTextFor } from '@/shared/utils/badgeText';
import StatusPill from '@/components/common/StatusPill';
import LoadingState from '@/components/common/LoadingState';
import LoadError from '@/components/common/LoadError';
import EmptyState from '@/components/common/EmptyState';
import StatValue from '@/components/common/StatValue';
import {
    StageLegend,
    StudentCardPositions,
} from './student/PositionApplicationsList';
import StudentNextStepCard from './student/StudentNextStepCard';
import scrollBehavior from '@/shared/utils/scrollBehavior';
import useMyCandidacies from '@/shared/hooks/useMyCandidacies';
import {
    canResubmit,
    returnedNoteFor,
    requestOutcomeOf,
} from '@/shared/utils/requestOutcome';
import DecisionHistory from '@/components/common/DecisionHistory';
import settledRead from '@/shared/utils/settledRead';
import { formatAnswerDay } from '@/shared/utils/deadline';
import { positionChoiceSummary } from '@/shared/utils/positionChoiceSummary';
import { requestInstructorLabel } from '@/shared/utils/requestInstructor';

const PROGRAM_CONFIG = {
    si: { bg: 'success', label: 'SI', full: 'Supplemental Instruction' },
    ta: { bg: 'primary', label: 'TA', full: 'Teaching Assistant' },
    // No Bootstrap fill: a bg-* utility is !important and would hide the
    // program colour that badge-la sets
    la: {
        bg: '',
        label: 'LA',
        full: 'Learning Assistant',
        className: 'badge-la',
    },
};

function ApplicationsPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { userProfile, effectiveRoles } = useAuth();
    const roles = resolveRoles({ effectiveRoles, userProfile });
    // success: set by the application wizard after a submit or resubmit.
    // notice: set by /apply when the student already has an application.
    // tab: which tab to open on.
    const arrival = useConsumeLocationState(['success', 'notice', 'tab']);
    const [successMessage, setSuccessMessage] = useState(
        arrival.success || null
    );
    const [notice, setNotice] = useState(arrival.notice || null);
    // The student half of this page — the applications a student submitted for
    // a position, and the offers they respond to — is the gated feature. The
    // course-support half above it is finished work and is unaffected.
    const studentApplicationEnabled = useFeature('studentApplication');
    const hiringEnabled = useFeature('hiring');
    // Where a student starts an application: Open Positions when its route
    // admits them, else the catalog.
    const studentApplyHref = applyEntryHref({
        hiring: hiringEnabled,
        studentApplication: studentApplicationEnabled,
        roles,
        fallback: '/courses',
    });
    const myCandidacies = useMyCandidacies({
        skip: !(studentApplicationEnabled && hiringEnabled),
    });
    const { currentTerm, currentTermCode, termsLoading } = useTerm();
    const [applications, setApplications] = useState([]);
    const [studentApplications, setStudentApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [courseFailed, setCourseFailed] = useState(false);
    const [studentFailed, setStudentFailed] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    // "Save and finish later" lands on the Drafts tab, a withdraw on Past
    const [activeTab, setActiveTab] = useState(
        ['drafts', 'past'].includes(arrival.tab) ? arrival.tab : 'current'
    );
    const [respondingId, setRespondingId] = useState(null);
    const [openOfferId, setOpenOfferId] = useState(null);
    const [scrollToId, setScrollToId] = useState(null);

    const isFaculty = roles.some((r) => ['faculty', 'admin'].includes(r));

    const handleOfferResponse = async (applicationId, action) => {
        setRespondingId(applicationId);
        try {
            const updated = await studentApplicationService.respondToOffer(
                applicationId,
                action
            );
            setStudentApplications((prev) =>
                prev.map((a) => (a._id === updated._id ? updated : a))
            );
            setError(null);
        } catch (_err) {
            setError('Failed to respond to offer. Please try again.');
        } finally {
            setRespondingId(null);
        }
    };

    useEffect(() => {
        const loadApplications = async () => {
            try {
                setLoading(true);
                setError(null);
                // Each half is settled on its own, so one failed read leaves
                // the other half on the page
                // A read that throws before returning its promise fails too
                const settle = (read) =>
                    settledRead(Promise.resolve().then(read));
                const [courseApps, studentApps] = await Promise.all([
                    settle(() => applicationService.getMyApplications()),
                    // Not asked for while the feature is off: the server gates
                    // this endpoint with the same flag, so the request can only
                    // 404, and a student cannot have an application to list
                    // when the page that creates one does not exist.
                    settle(() =>
                        studentApplicationEnabled
                            ? studentApplicationService.getMyApplications()
                            : []
                    ),
                ]);
                setApplications(courseApps.value || []);
                setStudentApplications(studentApps.value || []);
                setCourseFailed(courseApps.failed);
                setStudentFailed(studentApps.failed);
            } finally {
                setLoading(false);
            }
        };

        loadApplications();
    }, [studentApplicationEnabled, reloadKey]);

    // The next-step card opens the offer's row and brings it into view
    useEffect(() => {
        if (!scrollToId) return;
        document.getElementById(`position-${scrollToId}`)?.scrollIntoView({
            behavior: scrollBehavior(),
            block: 'start',
        });
        setScrollToId(null);
    }, [scrollToId]);

    // A notice links here with ?position=<candidacy id>: open that position's
    // row, once the rows exist
    const linkedPosition = new URLSearchParams(location.search).get('position');
    const linkHandled = useRef(false);
    useEffect(() => {
        if (
            !linkedPosition ||
            linkHandled.current ||
            loading ||
            myCandidacies.loading
        ) {
            return;
        }
        const linked = myCandidacies.candidacies.find(
            (c) => c.id === linkedPosition
        );
        if (!linked) return;
        linkHandled.current = true;
        if (linked.stage === 'offered') setOpenOfferId(linked.id);
        setScrollToId(linked.id);
    }, [
        linkedPosition,
        loading,
        myCandidacies.loading,
        myCandidacies.candidacies,
    ]);

    const reviewOffer = (candidacyId) => {
        setActiveTab('current');
        setOpenOfferId(candidacyId);
        setScrollToId(candidacyId);
    };

    // A request that waits on its faculty member comes first; the rest keep
    // the server's order
    const currentApplications = applications
        .filter(
            (app) =>
                app.term === currentTermCode &&
                !['withdrawn', 'draft'].includes(app.status)
        )
        .sort(
            (a, b) =>
                Number(canResubmit(b, userProfile)) -
                Number(canResubmit(a, userProfile))
        );
    const pastApplications = applications.filter(
        (app) =>
            app.term !== currentTermCode || ['withdrawn'].includes(app.status)
    );
    const draftApplications = applications.filter(
        (app) => app.status === 'draft'
    );
    // A student's draft is listed under Drafts until it is submitted; an old
    // term's draft can no longer be continued and stays under Past
    const draftStudentApps = studentApplications.filter(
        (app) => app.status === 'draft' && app.term === currentTermCode
    );
    // The Drafts tab goes once the last draft is deleted or submitted
    const hasDrafts =
        draftApplications.length > 0 || draftStudentApps.length > 0;
    useEffect(() => {
        if (!loading && !hasDrafts && activeTab === 'drafts') {
            setActiveTab('current');
        }
    }, [loading, hasDrafts, activeTab]);

    const pastStudentApps = studentApplications.filter(
        (app) =>
            app.term !== currentTermCode || ['withdrawn'].includes(app.status)
    );

    // One status per request, saying how much was awarded when it was
    const getStatusBadge = (application) => {
        const config = requestOutcomeOf(application);
        return (
            <Badge
                bg={config.bg}
                text={badgeTextFor(config.bg)}
                className="text-nowrap"
            >
                {config.label}
            </Badge>
        );
    };

    // The deleted draft's card held the focus, so it moves to the notice
    const [deletedNotice, setDeletedNotice] = useState(null);
    const deletedNoticeRef = useRevealAlert(deletedNotice);
    const removeApplication = (id) => {
        setApplications((prev) => prev.filter((a) => a._id !== id));
        setDeletedNotice({ text: 'Draft deleted.' });
    };

    const getCourseDisplay = (application) => {
        if (application.course) {
            return `${application.course.subject} ${application.course.courseNumber}`;
        }
        return 'Unknown Course';
    };

    const renderApplicationCard = (application) => (
        <Card key={application._id} className="mb-3 border-0 shadow-sm">
            <Card.Body>
                <Row className="align-items-center">
                    <Col md={6}>
                        <div className="d-flex align-items-start gap-3">
                            <div>
                                <Badge bg="secondary" className="mb-1">
                                    {getCourseDisplay(application)}
                                </Badge>
                                <p className="mb-1 fw-semibold">
                                    {application.course?.title || 'Course'}
                                </p>
                                <small className="text-muted">
                                    {requestInstructorLabel(
                                        application,
                                        userProfile
                                    )}
                                </small>
                                {/* Tells apart applications for different
                                    sections of the same course (#457) */}
                                {application.courseConfirmation
                                    ?.sectionNumber && (
                                    <small className="text-muted d-block">
                                        Section{' '}
                                        {
                                            application.courseConfirmation
                                                .sectionNumber
                                        }
                                    </small>
                                )}
                                {returnedNoteFor(application, userProfile) && (
                                    <small className="d-block mt-1">
                                        {returnedNoteFor(
                                            application,
                                            userProfile
                                        )}
                                    </small>
                                )}
                            </div>
                        </div>
                    </Col>
                    <Col md={3}>
                        <div className="mb-2">
                            <small className="text-muted d-block">
                                Preferences:
                            </small>
                            {application.programPreferences?.map((pref) => {
                                const config = PROGRAM_CONFIG[pref.programType];
                                return (
                                    <Badge
                                        key={pref.programType}
                                        bg={config ? config.bg : 'secondary'}
                                        text={badgeTextFor(
                                            config ? config.bg : 'secondary'
                                        )}
                                        className={`me-1 ${config?.className || ''}`}
                                    >
                                        {pref.rank}.{' '}
                                        {config?.label ||
                                            pref.programType.toUpperCase()}
                                    </Badge>
                                );
                            })}
                        </div>
                        {/* With one program ranked, the status already says
                            what it decided */}
                        {application.decisions?.length > 0 &&
                            application.programPreferences?.length > 1 && (
                                <div>
                                    <small className="text-muted d-block">
                                        Decisions:
                                    </small>
                                    <DecisionHistory
                                        decisions={application.decisions}
                                        compact
                                    />
                                </div>
                            )}
                    </Col>
                    <Col md={3} className="text-md-end">
                        <div className="mb-2">
                            {getStatusBadge(application)}
                        </div>
                        <small className="text-muted d-block">
                            {application.submittedAt
                                ? `Submitted ${formatAnswerDay(application.submittedAt)}`
                                : `Updated ${formatAnswerDay(application.updatedAt)}`}
                        </small>
                        <div className="d-flex flex-wrap gap-2 justify-content-md-end mt-2">
                            {application.status === 'draft' ? (
                                <>
                                    <Button
                                        as={Link}
                                        to={resumeDraftPath(application._id)}
                                        state={cameFromState(location)}
                                        variant="primary"
                                        size="sm"
                                        aria-label={`Continue ${getCourseDisplay(application)} application`}
                                    >
                                        Continue
                                    </Button>
                                    <DeleteDraftButton
                                        applicationId={application._id}
                                        courseLabel={getCourseDisplay(
                                            application
                                        )}
                                        size="sm"
                                        onDeleted={removeApplication}
                                    />
                                </>
                            ) : canResubmit(application, userProfile) ? (
                                <Button
                                    as={Link}
                                    to={`/applications/${application._id}`}
                                    state={{ startRevision: true }}
                                    variant="primary"
                                    size="sm"
                                    aria-label={`Edit and resubmit ${getCourseDisplay(application)} request`}
                                >
                                    Edit and resubmit
                                </Button>
                            ) : (
                                <Button
                                    as={Link}
                                    to={`/applications/${application._id}`}
                                    variant="outline-primary"
                                    size="sm"
                                    aria-label={`View ${getCourseDisplay(application)} request`}
                                >
                                    View
                                </Button>
                            )}
                            {/* A withdrawn request comes back for revision
                                with its section details; Duplicate starts a
                                new one without them */}
                            {isFaculty &&
                                application.status === 'withdrawn' &&
                                application.submittedAt && (
                                    <StartRevisionButton
                                        mode="reopen"
                                        size="sm"
                                        applicationId={application._id}
                                        courseLabel={getCourseDisplay(
                                            application
                                        )}
                                        onStarted={(result) =>
                                            navigate(
                                                `/applications/${application._id}`,
                                                {
                                                    state: {
                                                        startRevision: true,
                                                        reopened:
                                                            result.reopened,
                                                    },
                                                }
                                            )
                                        }
                                        onRefused={() =>
                                            window.location.reload()
                                        }
                                    />
                                )}
                            {/* Starts a new application prefilled from this
                                one (#457). /applications/new is faculty and
                                admin only. */}
                            {isFaculty && (
                                <Button
                                    as={Link}
                                    to={`/applications/new?duplicateFrom=${application._id}`}
                                    state={cameFromState(location)}
                                    variant="outline-primary"
                                    size="sm"
                                    aria-label={`Duplicate ${getCourseDisplay(application)} request`}
                                >
                                    Duplicate
                                </Button>
                            )}
                        </div>
                    </Col>
                </Row>

                {application.submitBlocker && (
                    <Alert variant="warning" className="mt-3 mb-0">
                        <strong>Can&apos;t be submitted as it stands:</strong>{' '}
                        {application.submitBlocker}
                    </Alert>
                )}

                {application.status === 'revision_requested' &&
                    application.revisionNotes && (
                        <Alert variant="warning" className="mt-3 mb-0">
                            <strong>Revision requested:</strong>{' '}
                            {application.revisionNotes}
                        </Alert>
                    )}
            </Card.Body>
        </Card>
    );

    const currentStudentApps = studentApplications.filter(
        (app) =>
            app.term === currentTermCode &&
            !['withdrawn', 'draft'].includes(app.status)
    );

    const renderStudentApplicationCard = (application) => {
        const programConfig = PROGRAM_CONFIG[application.program];
        return (
            <Card key={application._id} className="mb-3 border-0 shadow-sm">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col md={6}>
                            <div>
                                <Badge
                                    bg={
                                        programConfig
                                            ? programConfig.bg
                                            : 'secondary'
                                    }
                                    text={badgeTextFor(
                                        programConfig
                                            ? programConfig.bg
                                            : 'secondary'
                                    )}
                                    className={`mb-1 me-2 ${programConfig?.className || ''}`}
                                >
                                    {programConfig?.label ||
                                        application.program?.toUpperCase()}
                                </Badge>
                                <Badge
                                    bg="outline-secondary"
                                    text="dark"
                                    className="border"
                                >
                                    Student Application
                                </Badge>
                                <p className="mb-1 mt-2 fw-semibold">
                                    {programConfig?.full || 'Peer Supporter'}{' '}
                                    Application
                                </p>
                                {!hiringEnabled && (
                                    <small className="text-muted">
                                        {application.coursePreferences?.length >
                                        0
                                            ? `${application.coursePreferences.length} course preference${application.coursePreferences.length === 1 ? '' : 's'}`
                                            : positionChoiceSummary(
                                                  application
                                              ) || 'No positions chosen yet'}
                                    </small>
                                )}
                            </div>
                        </Col>
                        <Col md={3}>
                            {application.coursePreferences?.slice(0, 3).map(
                                (pref) =>
                                    pref.course && (
                                        <Badge
                                            key={pref.course._id || pref.rank}
                                            bg="light"
                                            text="dark"
                                            className="me-1 mb-1 border"
                                        >
                                            {pref.rank}. {pref.course.subject}{' '}
                                            {pref.course.courseNumber}
                                        </Badge>
                                    )
                            )}
                        </Col>
                        <Col md={3} className="text-md-end">
                            <div className="mb-2">
                                <StatusPill
                                    family="studentApplication"
                                    value={application.status}
                                />
                            </div>
                            <small className="text-muted d-block">
                                {application.submittedAt
                                    ? `Submitted ${formatAnswerDay(application.submittedAt)}`
                                    : `Updated ${formatAnswerDay(application.updatedAt)}`}
                            </small>
                            {application.term === currentTermCode &&
                                ['draft', 'submitted'].includes(
                                    application.status
                                ) && (
                                    <Button
                                        as={Link}
                                        to={`/apply/${application._id}/edit`}
                                        size="sm"
                                        variant="outline-primary"
                                        className="mt-2"
                                        aria-label={
                                            application.status === 'draft'
                                                ? `Continue ${programConfig?.label || ''} student application`
                                                : undefined
                                        }
                                    >
                                        {application.status === 'draft'
                                            ? 'Continue'
                                            : 'Edit Application'}
                                    </Button>
                                )}
                            {application.status === 'offered' &&
                                hiringEnabled && (
                                    <p className="small text-body-secondary mt-2 mb-0">
                                        {`Contact the ${application.program?.toUpperCase()} program about this offer.`}
                                    </p>
                                )}
                            {application.status === 'offered' &&
                                !hiringEnabled && (
                                    <div className="mt-2 d-flex gap-2 justify-content-md-end">
                                        <Button
                                            disabled={
                                                respondingId === application._id
                                            }
                                            size="sm"
                                            variant="success"
                                            onClick={() =>
                                                handleOfferResponse(
                                                    application._id,
                                                    'accept'
                                                )
                                            }
                                        >
                                            Accept
                                        </Button>
                                        <Button
                                            disabled={
                                                respondingId === application._id
                                            }
                                            size="sm"
                                            variant="danger"
                                            onClick={() =>
                                                handleOfferResponse(
                                                    application._id,
                                                    'decline'
                                                )
                                            }
                                        >
                                            Decline
                                        </Button>
                                    </div>
                                )}
                        </Col>
                    </Row>
                    {hiringEnabled && application.status !== 'draft' && (
                        <StudentCardPositions
                            application={application}
                            myCandidacies={myCandidacies}
                            openOfferId={openOfferId}
                            onOpenOfferChange={setOpenOfferId}
                        />
                    )}
                </Card.Body>
            </Card>
        );
    };

    const figuresLoading = loading || termsLoading;
    // Only faculty and admins file support requests (the server refuses
    // anyone else), so that read matters only to them. Anyone may hold
    // position applications while the feature is on.
    const courseMatters = isFaculty;
    const studentMatters = studentApplicationEnabled;
    const failedHalves = [
        courseMatters && courseFailed && 'your support requests',
        studentMatters && studentFailed && 'your position applications',
    ].filter(Boolean);
    const matteringHalves = [courseMatters, studentMatters].filter(Boolean);
    const allHalvesFailed =
        failedHalves.length > 0 &&
        failedHalves.length === matteringHalves.length;
    // A failed read is never an empty list: with the other half empty too,
    // the page shows the error alone, not tabs reading "0" and "no requests"
    const nothingListed =
        applications.length === 0 && studentApplications.length === 0;
    const loadFailed =
        allHalvesFailed || (failedHalves.length > 0 && nothingListed);
    // One half failed: list the other, and say which one is missing
    const partFailed = failedHalves.length > 0 && !loadFailed;
    const figuresFailed = loadFailed || partFailed;
    const allCurrentApps = [
        ...currentApplications.map((a) => ({
            ...a,
            _type: 'course',
        })),
        ...currentStudentApps.map((a) => ({
            ...a,
            _type: 'student',
        })),
    ];

    return (
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <h1>
                        {isFaculty ? 'Support requests' : 'My applications'}
                    </h1>
                    <p className="text-muted">
                        {isFaculty
                            ? 'View and manage your support requests'
                            : 'Your offers, interviews and the positions you applied to'}
                    </p>
                </div>
                {/* Faculty go straight to starting a request, which asks
                    only for the course; the catalog is one click further
                    from there for anyone who wants to browse. Students start
                    from Open Positions when hiring is on and the positions
                    page admits them, else from the catalog, where they
                    choose a course to apply to. */}
                {(isFaculty || studentApplicationEnabled) && (
                    <Button
                        as={Link}
                        to={
                            isFaculty ? '/applications/start' : studentApplyHref
                        }
                        variant="primary"
                    >
                        {isFaculty ? 'New support request' : 'Browse positions'}
                    </Button>
                )}
            </div>

            {successMessage && (
                <Alert
                    variant="success"
                    dismissible
                    onClose={() => setSuccessMessage(null)}
                >
                    {successMessage}
                </Alert>
            )}

            {notice && (
                <Alert
                    variant="info"
                    dismissible
                    onClose={() => setNotice(null)}
                >
                    {notice}
                </Alert>
            )}

            {deletedNotice && (
                <Alert
                    ref={deletedNoticeRef}
                    tabIndex={-1}
                    variant="success"
                    dismissible
                    onClose={() => setDeletedNotice(null)}
                >
                    {deletedNotice.text}
                </Alert>
            )}

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {/* Application Stats */}
            {isFaculty && (
                <Row className="mb-4">
                    <Col md={4}>
                        <Card className="border-0 shadow-sm text-center">
                            <Card.Body>
                                <p className="fs-3 fw-bold text-primary mb-1">
                                    <StatValue
                                        loading={figuresLoading}
                                        failed={figuresFailed}
                                        value={allCurrentApps.length}
                                    />
                                </p>
                                <small className="text-muted">
                                    Active requests
                                </small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="border-0 shadow-sm text-center">
                            <Card.Body>
                                <p className="fs-3 fw-bold text-success mb-1">
                                    <StatValue
                                        loading={figuresLoading}
                                        failed={figuresFailed}
                                        value={
                                            applications.filter(
                                                (a) => a.status === 'approved'
                                            ).length +
                                            studentApplications.filter((a) =>
                                                [
                                                    'offered',
                                                    'accepted',
                                                ].includes(a.status)
                                            ).length
                                        }
                                    />
                                </p>
                                <small className="text-muted">Approved</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="border-0 shadow-sm text-center">
                            <Card.Body>
                                <p className="fs-3 fw-bold text-info mb-1">
                                    <StatValue
                                        loading={figuresLoading}
                                        failed={figuresFailed}
                                        value={
                                            applications.filter(
                                                (a) =>
                                                    a.status === 'under_review'
                                            ).length +
                                            studentApplications.filter(
                                                (a) =>
                                                    a.status === 'under_review'
                                            ).length
                                        }
                                    />
                                </p>
                                <small className="text-muted">
                                    Under Review
                                </small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {!isFaculty &&
                hiringEnabled &&
                !figuresLoading &&
                !loadFailed &&
                !myCandidacies.loading &&
                !myCandidacies.error && (
                    <StudentNextStepCard
                        candidacies={myCandidacies.candidacies.filter(
                            (c) => c.term === currentTermCode
                        )}
                        onReviewOffer={reviewOffer}
                    />
                )}

            {figuresLoading ? (
                <LoadingState label="Loading applications" />
            ) : loadFailed ? (
                <LoadError
                    what={
                        allHalvesFailed ? 'your applications' : failedHalves[0]
                    }
                    onRetry={() => setReloadKey((k) => k + 1)}
                />
            ) : (
                <>
                    {partFailed && (
                        <LoadError
                            message={`We could not load ${failedHalves[0]}. The rest of this page is up to date.`}
                            onRetry={() => setReloadKey((k) => k + 1)}
                        />
                    )}
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className="mb-4"
                    >
                        <Tab
                            eventKey="current"
                            title={`${termDisplayName(currentTerm) || 'This term'} (${allCurrentApps.length})`}
                        >
                            {allCurrentApps.length === 0 &&
                            !isFaculty &&
                            !studentApplicationEnabled ? (
                                <Card className="border-0 bg-light">
                                    <Card.Body className="py-4">
                                        <StudentApplicationsNotOpen />
                                    </Card.Body>
                                </Card>
                            ) : allCurrentApps.length === 0 ? (
                                <EmptyState
                                    message={`You haven't submitted any ${isFaculty ? 'support requests' : 'applications'} for ${termDisplayName(currentTerm) || 'this term'}.`}
                                    action={{
                                        to: isFaculty
                                            ? '/applications/start'
                                            : studentApplyHref,
                                        label: isFaculty
                                            ? 'New support request'
                                            : studentApplyHref === '/positions'
                                              ? 'Open Positions'
                                              : 'Browse Courses',
                                    }}
                                />
                            ) : (
                                allCurrentApps.map((app) =>
                                    app._type === 'student'
                                        ? renderStudentApplicationCard(app)
                                        : renderApplicationCard(app)
                                )
                            )}
                        </Tab>
                        <Tab
                            eventKey="past"
                            title={`Past (${pastApplications.length + pastStudentApps.length})`}
                        >
                            {pastApplications.length === 0 &&
                            pastStudentApps.length === 0 ? (
                                <EmptyState message="No past applications. Applications from earlier terms and withdrawn ones are kept here." />
                            ) : (
                                <>
                                    {pastApplications.map(
                                        renderApplicationCard
                                    )}
                                    {pastStudentApps.map(
                                        renderStudentApplicationCard
                                    )}
                                </>
                            )}
                        </Tab>
                        {hasDrafts && (
                            <Tab
                                eventKey="drafts"
                                title={`Drafts (${draftApplications.length + draftStudentApps.length})`}
                            >
                                {draftApplications.map(renderApplicationCard)}
                                {draftStudentApps.map(
                                    renderStudentApplicationCard
                                )}
                            </Tab>
                        )}
                    </Tabs>
                </>
            )}

            {/* Help Card */}
            {isFaculty ? (
                <Card className="mt-4 bg-light border-0">
                    <Card.Body>
                        <h2 className="h5 fw-bold mb-3">
                            How support requests work
                        </h2>
                        <Row>
                            <Col md={4} className="mb-3 mb-md-0">
                                <h3 className="h6">Request status</h3>
                                <ul className="small text-muted ps-3">
                                    <li>
                                        <strong>Draft:</strong> Not submitted
                                        yet
                                    </li>
                                    <li>
                                        <strong>Submitted:</strong> Waiting for
                                        the programs you ranked to review it
                                    </li>
                                    <li>
                                        <strong>Under Review:</strong> A program
                                        is reviewing it
                                    </li>
                                    <li>
                                        <strong>Revision Requested:</strong> A
                                        program returned it for changes. Edit
                                        and resubmit it.
                                    </li>
                                    <li>
                                        <strong>Decided:</strong> At least one
                                        program has decided and another is still
                                        reviewing
                                    </li>
                                </ul>
                            </Col>
                            <Col md={4} className="mb-3 mb-md-0">
                                <h3 className="h6">Program Decisions</h3>
                                <ul className="small text-muted ps-3">
                                    <li>
                                        <strong>Approved:</strong> A program
                                        will support your course
                                    </li>
                                    <li>
                                        <strong>Partially approved:</strong> A
                                        program will support your course with
                                        fewer people than you asked for
                                    </li>
                                    <li>
                                        <strong>Contingent:</strong> A lower
                                        ranked program offered support. It
                                        becomes final only if a program you
                                        ranked higher does not award support.
                                    </li>
                                    <li>
                                        <strong>Not selected:</strong> The
                                        program cannot support your course this
                                        term
                                    </li>
                                </ul>
                            </Col>
                            <Col md={4}>
                                <h3 className="h6">When You Hear</h3>
                                <p className="small text-muted mb-0">
                                    Programs review requests when their windows
                                    open, not in rank order. Each program&apos;s
                                    decision is held until its release date,
                                    then released at 8:00 AM Mountain time with
                                    an email to you. The email you got when you
                                    submitted lists each release date.
                                </p>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            ) : hiringEnabled ? (
                <StageLegend />
            ) : (
                <Card className="mt-4 bg-light border-0">
                    <Card.Body>
                        <h2 className="h5 fw-bold mb-3">
                            Understanding the Application Process
                        </h2>
                        <Row>
                            <Col md={4} className="mb-3 mb-md-0">
                                <h3 className="h6">Application Status</h3>
                                <ul className="small text-muted ps-3">
                                    <li>
                                        <strong>Draft:</strong> Not yet
                                        submitted
                                    </li>
                                    <li>
                                        <strong>Submitted:</strong> Awaiting
                                        review
                                    </li>
                                    <li>
                                        <strong>Under Review:</strong> Being
                                        evaluated
                                    </li>
                                    <li>
                                        <strong>Revision Requested:</strong>{' '}
                                        Changes needed — see notes on your
                                        application
                                    </li>
                                    <li>
                                        <strong>Decided:</strong> Decisions made
                                    </li>
                                </ul>
                            </Col>
                            <Col md={4} className="mb-3 mb-md-0">
                                <h3 className="h6">Decision Types</h3>
                                <ul className="small text-muted ps-3">
                                    <li>
                                        <strong>Approved:</strong> You got the
                                        position!
                                    </li>
                                    <li>
                                        <strong>Contingent:</strong> Pending
                                        higher choices
                                    </li>
                                    <li>
                                        <strong>Waitlisted:</strong> On the
                                        waiting list
                                    </li>
                                    <li>
                                        <strong>Not Selected:</strong> Not
                                        chosen this time
                                    </li>
                                </ul>
                            </Col>
                            <Col md={4}>
                                <h3 className="h6">Your Preferences Matter</h3>
                                <p className="small text-muted mb-0">
                                    Programs review applications based on their
                                    timelines. If a higher-ranked program
                                    accepts you, lower-ranked contingent offers
                                    are automatically voided.
                                </p>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
}

export default ApplicationsPage;
